import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { Link } from 'react-router-dom';
import { SITES_QUERY } from '../graphql/queries';
import { DELETE_SITE } from '../graphql/mutations';
import M3Card from '../components/M3Card';
import M3Chip from '../components/M3Chip';
import M3LinearProgress from '../components/M3Progress';
import M3FAB from '../components/M3FAB';
import EmptyState from '../components/EmptyState';
import QueryError from '../components/QueryError';
import SiteFormDialog from '../components/SiteFormDialog';
import SitesMap from '../components/SitesMap';
import '../styles/sites.css';

export default function SitesPage() {
  const { data, loading, error } = useQuery(SITES_QUERY);
  const [deleteSite] = useMutation(DELETE_SITE, {
    refetchQueries: [{ query: SITES_QUERY }],
    awaitRefetchQueries: true
  });
  const [formSite, setFormSite] = useState(null);
  const openCreate = () => setFormSite({ id: null });
  const openEdit = (site) => setFormSite({ id: site.id });

  if (error)
    return (
      <div className="page-content">
        <QueryError error={error} onRetry={() => window.location.reload()} />
      </div>
    );

  if (loading)
    return (
      <div className="page-content">
        <M3LinearProgress indeterminate />
      </div>
    );

  if (!data?.sites?.length) {
    return (
      <div className="page-content">
        <EmptyState
          icon="location_on"
          title="No Sites"
          message="No archaeological sites have been added yet."
          action={
            <button className="btn btn-primary mt-3" onClick={openCreate}>
              Add Site
            </button>
          }
        />
        <M3FAB
          variant="primary"
          icon={<span className="material-symbols-rounded">add</span>}
          label="Add Site"
          onClick={openCreate}
          className="sites-fab"
        />
        <SiteFormDialog open={formSite !== null} site={formSite} onClose={() => setFormSite(null)} />
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="sites-header">
        <h1>Sites</h1>
        <button className="btn btn-primary" onClick={openCreate}>
          <span className="material-symbols-rounded">add</span> Add Site
        </button>
      </div>

      <SitesMap />

      <div className="sites-grid">
        {data.sites.map((site) => (
          <M3Card key={site.id} variant="outlined" className="site-card">
            <M3Card.Header
              title={site.name}
              subtitle={`ID: ${site.id}`}
              action={
                <div className="site-card-actions">
                  <Link to={`/sites/${site.id}`} className="site-card-action" title="View">
                    <span className="material-symbols-rounded">visibility</span>
                  </Link>
                  <button
                    onClick={() => openEdit(site)}
                    className="site-card-action"
                    title="Edit"
                  >
                    <span className="material-symbols-rounded">edit</span>
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${site.name}"? This cannot be undone.`)) {
                        deleteSite({ variables: { input: { id: site.id } } });
                      }
                    }}
                    className="site-card-action site-card-action-danger"
                    title="Delete"
                  >
                    <span className="material-symbols-rounded">delete</span>
                  </button>
                </div>
              }
            />
            <M3Card.Body>
              <div className="site-stats">
                <M3Chip variant="input">{`${site.gravesCount || 0} graves`}</M3Chip>
                <M3Chip variant="input">{`${site.figuresCount || 0} figures`}</M3Chip>
              </div>
            </M3Card.Body>
          </M3Card>
        ))}
      </div>

      <SiteFormDialog open={formSite !== null} site={formSite} onClose={() => setFormSite(null)} />
    </div>
  );
}
